import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { inventoryAPI, suppliersAPI, contractsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, Save } from 'lucide-react';
import { toast } from 'sonner';
import { showApiError } from '@/lib/errorHandler';
import { SearchableSelect } from '@/components/SearchableSelect';

const ITEM_TYPES = ['Fixed Asset', 'Raw Material', 'Service', 'Consumable', 'Semi-Finished Part', 'Finished Good', 'Scrap', 'Job Work Item'];
const UOMS = ['Nos', 'Kg', 'Grams', 'Meters', 'mm', 'Liters', 'Sets', 'Pairs', 'Rolls', 'Sheets', 'Feet', 'Inches', 'Sq.m', 'Sq.ft', 'Cu.m'];
const SOURCING_OPTIONS = [
  { value: 'buy', label: 'Buy' },
  { value: 'make', label: 'Make' },
  { value: 'receive_from_customer', label: 'Receive from Customer' },
];

export default function ItemFormPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const copyFromId = searchParams.get('copyFrom');

  const [form, setForm] = useState({
    item_name: '', item_type: 'Raw Material', unique_id: '', external_id: '',
    description: '', drawing_number: '', revision: '', unit_of_measure: 'Nos',
    secondary_uom: '', conversion_factor: '',
    is_inventory_item: true, make_or_buy: 'buy', preferred_supplier_id: '',
    preferred_supplier_name: '', category: '', min_stock_level: 0, reorder_level: 0,
    hsn_code: '', material_spec: '', is_cutting_tool: false, max_regrinds: 2,
    customer_id: '', customer_name: '',
    auto_indent: false, perform_qc_on_receipt: false, calibration_required: false,
    auto_notify_low_stock: false,
    shelf_life_not_applicable: true, shelf_life_months: '',
  });
  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [copiedFromName, setCopiedFromName] = useState('');

  useEffect(() => {
    loadSuppliers();
    loadCustomers();
    loadCategories();
    if (isEdit) {
      loadItem();
    } else if (copyFromId) {
      loadItemForCopy();
    }
  }, [id, copyFromId]);

  const loadSuppliers = async () => {
    try {
      const res = await suppliersAPI.approvedList();
      setSuppliers(res.data || []);
    } catch { /* ignore - suppliers may not be accessible */ }
  };

  const loadCustomers = async () => {
    try {
      const res = await contractsAPI.activeCustomers();
      setCustomers(res.data || []);
    } catch { /* ignore - customers may not be accessible */ }
  };

  const loadCategories = async () => {
    try {
      const res = await inventoryAPI.listCategories();
      setCategories(res.data?.categories || []);
    } catch { /* ignore */ }
  };

  const loadItem = async () => {
    try {
      const res = await inventoryAPI.getItem(id);
      setForm(prev => ({
        ...prev,
        item_name: res.data.item_name || '',
        item_type: res.data.item_type || 'Raw Material',
        unique_id: res.data.unique_id || '',
        external_id: res.data.external_id || '',
        description: res.data.description || '',
        drawing_number: res.data.drawing_number || '',
        revision: res.data.revision || '',
        unit_of_measure: res.data.unit_of_measure || 'Nos',
        secondary_uom: res.data.secondary_uom || '',
        conversion_factor: res.data.conversion_factor || '',
        is_inventory_item: res.data.is_inventory_item !== false,
        make_or_buy: res.data.make_or_buy || 'buy',
        preferred_supplier_id: res.data.preferred_supplier_id || '',
        preferred_supplier_name: res.data.preferred_supplier_name || '',
        category: res.data.category || '',
        min_stock_level: res.data.min_stock_level || 0,
        reorder_level: res.data.reorder_level || 0,
        hsn_code: res.data.hsn_code || '',
        material_spec: res.data.material_spec || '',
        is_cutting_tool: res.data.is_cutting_tool || false,
        max_regrinds: res.data.max_regrinds || 2,
        customer_id: res.data.customer_id || '',
        customer_name: res.data.customer_name || '',
        auto_indent: res.data.auto_indent || false,
        perform_qc_on_receipt: res.data.perform_qc_on_receipt || false,
        calibration_required: res.data.calibration_required || false,
        auto_notify_low_stock: res.data.auto_notify_low_stock || false,
        shelf_life_not_applicable: res.data.shelf_life_not_applicable !== false,
        shelf_life_months: res.data.shelf_life_months ?? '',
      }));
    } catch (err) {
      toast.error('Failed to load item');
      navigate('/inventory/items');
    }
  };

  const loadItemForCopy = async () => {
    try {
      const res = await inventoryAPI.getItem(copyFromId);
      setCopiedFromName(res.data.item_name || '');
      // Copy master data fields but clear unique_id for new entry
      setForm(prev => ({
        ...prev,
        item_name: res.data.item_name || '',
        item_type: res.data.item_type || 'Raw Material',
        unique_id: '', // Clear - user must enter new unique ID
        external_id: res.data.external_id || '',
        description: res.data.description || '',
        drawing_number: res.data.drawing_number || '',
        revision: res.data.revision || '',
        unit_of_measure: res.data.unit_of_measure || 'Nos',
        secondary_uom: res.data.secondary_uom || '',
        conversion_factor: res.data.conversion_factor || '',
        is_inventory_item: res.data.is_inventory_item !== false,
        make_or_buy: res.data.make_or_buy || 'buy',
        preferred_supplier_id: res.data.preferred_supplier_id || '',
        preferred_supplier_name: res.data.preferred_supplier_name || '',
        category: res.data.category || '',
        min_stock_level: res.data.min_stock_level || 0,
        reorder_level: res.data.reorder_level || 0,
        hsn_code: res.data.hsn_code || '',
        material_spec: res.data.material_spec || '',
        is_cutting_tool: res.data.is_cutting_tool || false,
        max_regrinds: res.data.max_regrinds || 2,
        customer_id: res.data.customer_id || '',
        customer_name: res.data.customer_name || '',
        auto_indent: res.data.auto_indent || false,
        perform_qc_on_receipt: res.data.perform_qc_on_receipt || false,
        calibration_required: res.data.calibration_required || false,
        auto_notify_low_stock: res.data.auto_notify_low_stock || false,
        shelf_life_not_applicable: res.data.shelf_life_not_applicable !== false,
        shelf_life_months: res.data.shelf_life_months ?? '',
      }));
      toast.info(`Creating copy of "${res.data.item_name}". Please enter a new Unique ID.`);
    } catch (err) {
      toast.error('Failed to load source item for copy');
      navigate('/inventory/items');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.item_name || !form.unique_id || !form.item_type) {
      toast.error('Item name, unique ID, and type are required');
      return;
    }
    if (form.secondary_uom && (!form.conversion_factor || parseFloat(form.conversion_factor) <= 0)) {
      toast.error('Conversion factor is required when secondary UOM is defined');
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (payload.conversion_factor) payload.conversion_factor = parseFloat(payload.conversion_factor);
      if (!payload.secondary_uom) { payload.secondary_uom = ''; payload.conversion_factor = null; }
      payload.shelf_life_months = (payload.shelf_life_not_applicable || payload.shelf_life_months === '' || payload.shelf_life_months === null)
        ? null
        : parseFloat(payload.shelf_life_months);
      if (isEdit) {
        await inventoryAPI.updateItem(id, payload);
        toast.success('Item updated');
        navigate(`/inventory/items/${id}`);
      } else {
        const res = await inventoryAPI.createItem(payload);
        toast.success('Item created');
        navigate(`/inventory/items/${res.data.id}`);
      }
    } catch (err) {
      showApiError(err, 'Failed to save item');
    } finally { setLoading(false); }
  };

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  return (
    <div className="space-y-4 max-w-4xl" data-testid="item-form-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="font-heading text-xl font-bold tracking-tight">
            {isEdit ? 'Edit Item' : copyFromId ? 'Copy Item' : 'New Item'}
          </h1>
          {copyFromId && copiedFromName && (
            <p className="text-xs text-muted-foreground">Creating copy from: {copiedFromName}</p>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Basic Info */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-sm font-medium">Basic Information</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Unique ID *</Label>
              <Input value={form.unique_id} onChange={e => updateField('unique_id', e.target.value)}
                disabled={isEdit} placeholder="e.g. RM-AL6061-001" className="h-8 text-xs font-mono" data-testid="item-unique-id" />
            </div>
            <div>
              <Label className="text-xs">Item Name *</Label>
              <Input value={form.item_name} onChange={e => updateField('item_name', e.target.value)}
                placeholder="e.g. AL 6061 Round Bar" className="h-8 text-xs" data-testid="item-name" />
            </div>
            <div>
              <Label className="text-xs">Item Type *</Label>
              <Select value={form.item_type} onValueChange={v => updateField('item_type', v)}>
                <SelectTrigger className="h-8 text-xs" data-testid="item-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ITEM_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">External Id (Name Given to this Item By External Supplier)</Label>
              <Input value={form.external_id} onChange={e => updateField('external_id', e.target.value)}
                placeholder="Supplier's part name" className="h-8 text-xs" data-testid="item-external-id" />
            </div>
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={form.category || '_none'} onValueChange={v => updateField('category', v === '_none' ? '' : v)}>
                <SelectTrigger className="h-8 text-xs" data-testid="item-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">-- No Category --</SelectItem>
                  {categories.map(c => (
                    <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Primary UOM</Label>
              <Select value={form.unit_of_measure} onValueChange={v => updateField('unit_of_measure', v)}>
                <SelectTrigger className="h-8 text-xs" data-testid="item-uom">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {UOMS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Secondary UOM <span className="text-muted-foreground">(optional)</span></Label>
              <Select value={form.secondary_uom || 'none'} onValueChange={v => {
                updateField('secondary_uom', v === 'none' ? '' : v);
                if (v === 'none') updateField('conversion_factor', '');
              }}>
                <SelectTrigger className="h-8 text-xs" data-testid="item-secondary-uom">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {UOMS.filter(u => u !== form.unit_of_measure).map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.secondary_uom && (
              <div>
                <Label className="text-xs">Conversion Factor *</Label>
                <Input type="number" step="any" className="h-8 text-xs" value={form.conversion_factor}
                  onChange={e => updateField('conversion_factor', e.target.value)}
                  placeholder={`1 ${form.unit_of_measure} = ? ${form.secondary_uom}`}
                  data-testid="item-conversion-factor" />
                <p className="text-[10px] text-muted-foreground mt-0.5">1 {form.unit_of_measure} = {form.conversion_factor || '?'} {form.secondary_uom}</p>
              </div>
            )}
            <div className="md:col-span-2">
              <Label className="text-xs">Description</Label>
              <Textarea value={form.description} onChange={e => updateField('description', e.target.value)}
                placeholder="Item description..." className="text-xs min-h-[60px]" data-testid="item-description" />
            </div>
          </CardContent>
        </Card>

        {/* Technical Details */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-sm font-medium">Technical Details</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Drawing Number</Label>
              <Input value={form.drawing_number} onChange={e => updateField('drawing_number', e.target.value)}
                className="h-8 text-xs font-mono" data-testid="item-drawing" />
            </div>
            <div>
              <Label className="text-xs">Revision</Label>
              <Input value={form.revision} onChange={e => updateField('revision', e.target.value)}
                className="h-8 text-xs" data-testid="item-revision" />
            </div>
            <div>
              <Label className="text-xs">Material Specification</Label>
              <Input value={form.material_spec} onChange={e => updateField('material_spec', e.target.value)}
                placeholder="e.g. AL 6061 T6" className="h-8 text-xs" data-testid="item-material-spec" />
            </div>
            <div>
              <Label className="text-xs">HSN Code</Label>
              <Input value={form.hsn_code} onChange={e => updateField('hsn_code', e.target.value)}
                className="h-8 text-xs font-mono" data-testid="item-hsn" />
            </div>
            <div className="md:col-span-2 border rounded p-2.5 bg-slate-50/60">
              <Label className="text-xs">Shelf Life <span className="text-muted-foreground">— printed on the Certificate of Conformance</span></Label>
              <div className="flex items-center gap-4 mt-1.5">
                <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 accent-violet-600"
                    checked={form.shelf_life_not_applicable}
                    onChange={e => {
                      updateField('shelf_life_not_applicable', e.target.checked);
                      if (e.target.checked) updateField('shelf_life_months', '');
                    }}
                    data-testid="item-shelf-life-na-checkbox"
                  />
                  Not applicable
                </label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    step="0.5"
                    value={form.shelf_life_months}
                    onChange={e => updateField('shelf_life_months', e.target.value)}
                    disabled={form.shelf_life_not_applicable}
                    placeholder="e.g. 12"
                    className="h-8 text-xs w-28"
                    data-testid="item-shelf-life-months"
                  />
                  <span className="text-xs text-muted-foreground">months</span>
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs">Customer</Label>
              <SearchableSelect
                options={[{ value: '', label: 'None' }, ...customers.map(c => ({ value: c.id, label: c.customer_name, sub: c.gst_no || '', searchStr: `${c.customer_name} ${c.gst_no || ''}` }))]}
                value={form.customer_id || ''}
                onSelect={v => {
                  const cust = customers.find(c => c.id === v);
                  updateField('customer_id', v || '');
                  updateField('customer_name', cust ? cust.customer_name : '');
                }}
                placeholder="Search customers..."
                emptyText="No customers found."
                testId="item-customer"
              />
            </div>
          </CardContent>
        </Card>

        {/* Inventory Settings */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-sm font-medium">Inventory & Sourcing</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-xs font-medium">Inventory Item</Label>
                <p className="text-[10px] text-muted-foreground">Track quantity (receipt/issue)</p>
              </div>
              <Switch checked={form.is_inventory_item} onCheckedChange={v => updateField('is_inventory_item', v)} data-testid="is-inventory-switch" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Sourcing</Label>
                <Select value={form.make_or_buy} onValueChange={v => updateField('make_or_buy', v)}>
                  <SelectTrigger className="h-8 text-xs" data-testid="make-buy-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SOURCING_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {form.make_or_buy === 'buy' && (
                <div>
                  <Label className="text-xs">Preferred Supplier</Label>
                  <SearchableSelect
                    options={[{ value: '', label: 'None' }, ...suppliers.map(s => ({ value: s.id, label: s.supplier_name, searchStr: `${s.supplier_name} ${s.supplier_code || ''}` }))]}
                    value={form.preferred_supplier_id || ''}
                    onSelect={v => {
                      const sup = suppliers.find(s => s.id === v);
                      updateField('preferred_supplier_id', v || '');
                      updateField('preferred_supplier_name', sup ? sup.supplier_name : '');
                    }}
                    placeholder="Search suppliers..."
                    emptyText="No approved suppliers."
                    testId="supplier-select"
                  />
                </div>
              )}
              {form.make_or_buy === 'receive_from_customer' && (
                <div>
                  <Label className="text-xs">Customer (Source)</Label>
                  <SearchableSelect
                    options={[{ value: '', label: 'None' }, ...customers.map(c => ({ value: c.id, label: c.customer_name, searchStr: `${c.customer_name} ${c.gst_no || ''}` }))]}
                    value={form.customer_id || ''}
                    onSelect={v => {
                      const cust = customers.find(c => c.id === v);
                      updateField('customer_id', v || '');
                      updateField('customer_name', cust ? cust.customer_name : '');
                    }}
                    placeholder="Search customers..."
                    emptyText="No customers found."
                    testId="item-source-customer"
                  />
                  <label className="flex items-center gap-2 mt-2 text-[11px] cursor-pointer select-none" data-testid="auto-notify-low-stock-label">
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 accent-blue-600"
                      checked={!!form.auto_notify_low_stock}
                      onChange={e => updateField('auto_notify_low_stock', e.target.checked)}
                      data-testid="auto-notify-low-stock"
                    />
                    Auto-notify customer of low stock <span className="text-muted-foreground">(weekly email when below reorder level)</span>
                  </label>
                </div>
              )}
            </div>
            {form.is_inventory_item && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Min Stock Level</Label>
                  <Input type="number" value={form.min_stock_level} onChange={e => updateField('min_stock_level', parseFloat(e.target.value) || 0)}
                    className="h-8 text-xs" data-testid="min-stock" />
                </div>
                <div>
                  <Label className="text-xs">Reorder Level</Label>
                  <Input type="number" value={form.reorder_level} onChange={e => updateField('reorder_level', parseFloat(e.target.value) || 0)}
                    className="h-8 text-xs" data-testid="reorder-level" />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Cutting Tool Settings */}
        {form.item_type === 'Consumable' && (
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-sm font-medium">Cutting Tool Settings</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-xs font-medium">Is Cutting Tool</Label>
                  <p className="text-[10px] text-muted-foreground">Enable regrind tracking with auto-scrap</p>
                </div>
                <Switch checked={form.is_cutting_tool} onCheckedChange={v => updateField('is_cutting_tool', v)} data-testid="is-cutting-tool-switch" />
              </div>
              {form.is_cutting_tool && (
                <div>
                  <Label className="text-xs">Max Regrinds Before Scrap</Label>
                  <Input type="number" value={form.max_regrinds} onChange={e => updateField('max_regrinds', parseInt(e.target.value) || 2)}
                    min={1} max={10} className="h-8 text-xs w-32" data-testid="max-regrinds" />
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Purchase & Quality Settings */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Purchase & Quality Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-xs font-medium">Auto Indent</Label>
                <p className="text-[10px] text-muted-foreground">Automatically add to purchase indent when stock falls below minimum</p>
              </div>
              <Switch checked={form.auto_indent} onCheckedChange={v => updateField('auto_indent', v)} data-testid="auto-indent-switch" />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-xs font-medium">Perform QC on Receipt</Label>
                <p className="text-[10px] text-muted-foreground">Incoming receipt inspection (OK/Quarantine/Reject)</p>
              </div>
              <Switch checked={form.perform_qc_on_receipt} onCheckedChange={v => updateField('perform_qc_on_receipt', v)} data-testid="perform-qc-switch" />
            </div>
            {form.item_type === 'Fixed Asset' && (
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-xs font-medium">Calibration Required</Label>
                  <p className="text-[10px] text-muted-foreground">Track this asset in calibration app</p>
                </div>
                <Switch checked={form.calibration_required} onCheckedChange={v => updateField('calibration_required', v)} data-testid="calibration-required-switch" />
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
          <Button type="submit" disabled={loading} data-testid="save-item-btn">
            <Save className="w-4 h-4 mr-1" />
            {loading ? 'Saving...' : isEdit ? 'Update Item' : 'Create Item'}
          </Button>
        </div>
      </form>
    </div>
  );
}
